import { FlowComponentDefinition } from "../../interface/functionalityInterface";
import { OUTLINE_COLOR } from "./renderingUtils";

const tankFieldOptions = {
  topShape: [
    { label: "Round top", value: "round" },
    { label: "Flat top", value: "flat" },
  ],
  bottomShape: [
    { label: "Flat bottom", value: "flat" },
    { label: "Round bottom", value: "round" },
    { label: "Cone bottom", value: "cone" },
  ],
  count: [
    { label: "0", value: "0" },
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
  ],
};

const getCount = (value: string | undefined) => {
  const parsed = Number(value ?? "0");
  return Number.isFinite(parsed) ? Math.max(0, Math.min(4, parsed)) : 0;
};

const getTankMetrics = (
  topConnections: number,
  sideConnections: number,
  bottomConnections: number
) => {
  const widthUnits =
    topConnections > 2 || bottomConnections > 2 || sideConnections > 2 ? 3 : 1;
  const bodyWidth = Math.min(62 + (widthUnits - 1) * 10, 84);
  const left = (100 - bodyWidth) / 2;
  const right = left + bodyWidth;

  return { widthUnits, bodyWidth, left, right };
};

const getCenteredTopOrBottomXs = (count: number, widthUnits: number) => {
  if (count <= 0) return [] as number[];
  if (count === 1) return [50];
  if (count === 2) return [50, widthUnits === 1 ? 68 : 74];
  if (count === 3) return [26, 50, 74];
  return [18, 38, 50, 82];
};

const getSideNozzles = (count: number) => {
  if (count <= 0) return [] as { side: "left" | "right"; y: number }[];
  if (count === 1) return [{ side: "right" as const, y: 50 }];
  if (count === 2) {
    return [
      { side: "left" as const, y: 50 },
      { side: "right" as const, y: 50 },
    ];
  }
  if (count === 3) {
    return [
      { side: "left" as const, y: 34 },
      { side: "right" as const, y: 50 },
      { side: "left" as const, y: 66 },
    ];
  }

  return [
    { side: "left" as const, y: 30 },
    { side: "right" as const, y: 42 },
    { side: "left" as const, y: 58 },
    { side: "right" as const, y: 70 },
  ];
};

export const tankComponent: FlowComponentDefinition = {
  type: "tank",
  title: "tank",
  category: "Equipment",
  visualKind: "tank",
  defaultColor: "#d6ddd8",
  previewFlow: { from: "up", to: "down" },
  ports: ["up", "right", "down", "left"],
  incomingDirections: ["up"],
  outgoingDirections: ["down"],
  defaults: {
    topShape: "round",
    bottomShape: "flat",
    topConnections: "1",
    sideConnections: "2",
    bottomConnections: "1",
  },
  fields: [
    { key: "topShape", label: "Top shape", inputType: "select", options: tankFieldOptions.topShape },
    {
      key: "bottomShape",
      label: "Bottom shape",
      inputType: "select",
      options: tankFieldOptions.bottomShape,
    },
    {
      key: "topConnections",
      label: "Top nozzles",
      inputType: "select",
      options: tankFieldOptions.count,
    },
    {
      key: "sideConnections",
      label: "Side nozzles",
      inputType: "select",
      options: tankFieldOptions.count,
    },
    {
      key: "bottomConnections",
      label: "Bottom nozzles",
      inputType: "select",
      options: tankFieldOptions.count,
    },
    { key: "color", label: "Color", inputType: "color" },
  ],
  renderGraphic: ({ node, componentColor, arrowColor }) => {
    const topShape = node.values.topShape ?? "round";
    const bottomShape = node.values.bottomShape ?? "flat";
    const topConnections = getCount(node.values.topConnections);
    const sideConnections = getCount(node.values.sideConnections);
    const bottomConnections = getCount(node.values.bottomConnections);
    const { widthUnits, left, right, bodyWidth } = getTankMetrics(
      topConnections,
      sideConnections,
      bottomConnections
    );
    const bodyTop = topShape === "round" ? 24 : 18;
    const bodyBottom = bottomShape === "cone" ? 74 : bottomShape === "round" ? 70 : 82;
    const topXs = getCenteredTopOrBottomXs(topConnections, widthUnits);
    const bottomXs = getCenteredTopOrBottomXs(bottomConnections, widthUnits);
    const sideNozzles = getSideNozzles(sideConnections);

    return (
      <>
        {topXs.length > 0
          ? topXs.map((x, index) => {
              return (
                <line
                  key={`tank-top-${index}`}
                  x1={x}
                  y1="0"
                  x2={x}
                  y2={bodyTop}
                  stroke={OUTLINE_COLOR}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              );
            })
          : null}

        {bottomXs.length > 0
          ? bottomXs.map((x, index) => {
              const startY = bottomShape === "cone" ? 90 : bottomShape === "round" ? 92 : bodyBottom;
              return (
                <line
                  key={`tank-bottom-${index}`}
                  x1={x}
                  y1={startY}
                  x2={x}
                  y2="100"
                  stroke={OUTLINE_COLOR}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              );
            })
          : null}

        {sideNozzles.length > 0
          ? sideNozzles.map(({ side, y }, index) => {
              const onLeft = side === "left";
              return (
                <line
                  key={`tank-side-${index}`}
                  x1={onLeft ? 0 : right}
                  y1={y}
                  x2={onLeft ? left : 100}
                  y2={y}
                  stroke={OUTLINE_COLOR}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              );
            })
          : null}

        {topShape === "round" ? (
          <path
            d={`M ${left} ${bodyTop} Q 50 2 ${right} ${bodyTop}`}
            fill="#ffffff"
            stroke={OUTLINE_COLOR}
            strokeWidth="2.2"
          />
        ) : (
          <line
            x1={left}
            y1={bodyTop}
            x2={right}
            y2={bodyTop}
            stroke={OUTLINE_COLOR}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        )}

        <line x1={left} y1={bodyTop} x2={left} y2={bodyBottom} stroke={OUTLINE_COLOR} strokeWidth="2.2" />
        <line x1={right} y1={bodyTop} x2={right} y2={bodyBottom} stroke={OUTLINE_COLOR} strokeWidth="2.2" />
        <rect x={left} y={bodyTop} width={bodyWidth} height={bodyBottom - bodyTop} fill={componentColor} opacity="0.55" />

        {bottomShape === "cone" ? (
          <path
            d={`M ${left} ${bodyBottom} L 50 90 L ${right} ${bodyBottom}`}
            fill="#ffffff"
            stroke={OUTLINE_COLOR}
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
        ) : bottomShape === "round" ? (
          <path
            d={`M ${left} ${bodyBottom} Q 50 98 ${right} ${bodyBottom}`}
            fill="#ffffff"
            stroke={OUTLINE_COLOR}
            strokeWidth="2.2"
          />
        ) : (
          <line
            x1={left}
            y1={bodyBottom}
            x2={right}
            y2={bodyBottom}
            stroke={OUTLINE_COLOR}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        )}

        <polygon points="50,86 44,74 56,74" fill={arrowColor} />
      </>
    );
  },
  renderAnimatedFlow: ({ startPoint, endPoint, pulseColor }) => (
    <polyline
      points={`${startPoint.x},${startPoint.y} 50,50 ${endPoint.x},${endPoint.y}`}
      fill="none"
      stroke={pulseColor}
      strokeWidth="9"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  ),
};
